import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  StarIcon, 
  CalendarIcon, 
  ArrowTrendingUpIcon 
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axios';

const ProviderAnalytics = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalFavorites: 0,
    totalEventSaves: 0,
    activeEvents: 0,
    businessName: '',
    tier: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/user/analytics');
        setStats(res.data);
      } catch (err) {
        console.error("Error fetching provider analytics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-500 font-bold">
        {t('provider-analytics.loading')}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {stats.businessName || t('provider-analytics.title')}
        </h1>
        {stats.tier && (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold uppercase">
            {t(`map.${stats.tier.toLowerCase()}`, { defaultValue: stats.tier })}
          </span>
        )}
      </div>

      <h2 className="text-xl font-bold text-gray-400 mb-8">
        {t('provider-analytics.subtitle')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden flex flex-col justify-between min-h-[180px]">
          <div className="relative z-10">
            <div className="bg-rose-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
              <StarIcon className="w-6 h-6 text-rose-500" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">{t('provider-analytics.total-favorites')}</h3>
            <p className="text-4xl font-black text-gray-900 mt-2">{stats.totalFavorites}</p>
          </div>
          <div className="relative z-10 mt-4">
            <p className="text-xs text-rose-600 font-bold flex items-center gap-1">
              <ArrowTrendingUpIcon className="w-4 h-4" />
              {t('provider-analytics.favorites-desc')}
            </p>
          </div>
          <StarIcon className="absolute -right-6 -bottom-6 w-32 h-32 text-gray-50 opacity-40 pointer-events-none" />
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden flex flex-col justify-between min-h-[180px]">
          <div className="relative z-10">
            <div className="bg-violet-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6 text-violet-500" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">{t('provider-analytics.saved-events')}</h3>
            <p className="text-4xl font-black text-gray-900 mt-2">{stats.totalEventSaves}</p>
          </div>
          <div className="relative z-10 mt-4">
            <p className="text-xs text-violet-600 font-bold">
              {t('provider-analytics.events-desc', { count: stats.activeEvents })}
            </p>
          </div>
          <CalendarIcon className="absolute -right-6 -bottom-6 w-32 h-32 text-gray-50 opacity-40 pointer-events-none" />
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden flex flex-col justify-between min-h-[180px]">
          <div className="relative z-10">
            <div className="bg-emerald-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
              <UsersIcon className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">{t('provider-analytics.community-reach')}</h3>
            <p className="text-4xl font-black text-gray-900 mt-2">
              {stats.totalFavorites + stats.totalEventSaves}
            </p>
          </div>
          <div className="relative z-10 mt-4">
            <p className="text-xs text-emerald-600 font-bold">
              {t('provider-analytics.reach-desc')}
            </p>
          </div>
          <UsersIcon className="absolute -right-6 -bottom-6 w-32 h-32 text-gray-50 opacity-40 pointer-events-none" />
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="text-xl font-bold mb-2">{t('provider-analytics.cta-title')}</h3>
          <p className="text-gray-400 text-sm max-w-md">
            {t('provider-analytics.cta-desc')}
          </p>
        </div>
        <Link
          to="/provider/manage-events"
          className="bg-[#ff9a45] hover:bg-[#e68a3d] text-white px-8 py-3 rounded font-bold text-sm transition whitespace-nowrap text-center w-full md:w-auto"
        >
          {t('provider-analytics.manage-events')}
        </Link>
      </div>
    </div>
  );
};

export default ProviderAnalytics;
